import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const getAuthHeader = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  const token = user?.token;
  
  if (!token) {
    return {};
  }
  
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  };
};

/**
 * Get all users (super_admin only)
 * @returns {Promise<Array>} List of users
 */
export const getUsers = async () => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader.headers?.Authorization) {
      throw new Error('Not authenticated. Please log in again.');
    }

    
    const response = await axios.get(`${API_URL}/users`, authHeader);

    
    if (!response.data) {
      throw new Error('No data received from server');
    }
    
    // Handle different possible response formats
    if (Array.isArray(response.data)) {
      // Direct array response
      return response.data;
    } else if (response.data.users && Array.isArray(response.data.users)) {
      // Response with { success: true, users: [...] }
      return response.data.users;
    } else if (response.data.data && Array.isArray(response.data.data)) {
      // Response with { data: [...] }
      return response.data.data;
    }

    if (response.data) {
      return response.data;
    }
    
    throw new Error('Unexpected response format from server');
  } catch (error) {
    console.error('Error fetching users:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    });
    
    if (error.response?.status === 401) {
      // Clear invalid user data
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Your session has expired. Please log in again.');
    }
    
    throw new Error(error.response?.data?.message || 
                   error.message || 
                   'Failed to fetch users. Please try again later.');
  }
};

/**
 * Set or update user's Deriv token
 * @param {string} userDerivToken - The Deriv API token
 * @param {number} userId - The user ID to update
 * @returns {Promise<Object>} Response data
 */
/**
 * Get a user's Deriv token
 * @param {number} userId - The user ID
 * @returns {Promise<Object>} Response data with token
 */
export const getUserDerivToken = async (userId) => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader.headers?.Authorization) {
      throw new Error('Not authenticated. Please log in again.');
    }

    const response = await axios.get(
      `${API_URL}/user-deriv-token/${userId}`,
      authHeader
    );
    
    if (!response.data) {
      throw new Error('No response data from server');
    }
    
    if (response.data.success && response.data.data?.token) {
      return response.data.data.token;
    }
    
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      // Token not found for this user
      return null;
    }
    throw error;
  }
};

/**
 * Set or update user's Deriv token
 * @param {string} userDerivToken - The Deriv API token
 * @param {number} userId - The user ID to update
 * @returns {Promise<Object>} Response data
 */
export const setUserDerivToken = async (userDerivToken, userId) => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader.headers?.Authorization) {
      throw new Error('Not authenticated. Please log in again.');
    }

    const response = await axios.post(
      `${API_URL}/user-deriv-token`,
      { userDerivToken, user_id: userId },
      authHeader
    );
    
    if (!response.data) {
      throw new Error('No response data from server');
    }
    
    // Handle success case - either response has success: true or contains token data
    if (response.data.success || (response.data.id && response.data.token)) {
      return { 
        success: true,
        ...response.data 
      };
    }
    
    // If we have an error message from the server, use it
    if (response.data.message) {
      throw new Error(response.data.message);
    }
    
    throw new Error('Unexpected response format from server');
    
  } catch (error) {
    // Handle different types of errors
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const { status, data } = error.response;
      let errorMessage = `Request failed with status ${status}`;
      
      if (status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        // Optionally clear user data and redirect to login
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else if (data && data.message) {
        errorMessage = data.message;
      }
      
      throw new Error(errorMessage);
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Something happened in setting up the request
      throw new Error(error.message || 'Failed to update token');
    }
  }
};

/**
 * Delete a user
 * @param {number} userId - The user ID to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteUser = async (userId) => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader.headers?.Authorization) {
      throw new Error('Not authenticated. Please log in again.');
    }

    const response = await axios.delete(
      `${API_URL}/delete/user/${userId}`,
      authHeader
    );
    
    if (!response.data) {
      throw new Error('No response data from server');
    }
    
    return {
      success: true,
      ...response.data
    };
    
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      let errorMessage = `Request failed with status ${status}`;
      
      if (status === 401) {
        errorMessage = 'Session expired. Please log in again.';
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else if (status === 403) {
        errorMessage = 'You do not have permission to delete users.';
      } else if (status === 404) {
        errorMessage = 'User not found.';
      } else if (data && data.message) {
        errorMessage = data.message;
      }
      
      throw new Error(errorMessage);
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to delete user');
    }
  }
};
